import React, { useEffect, useState, useCallback, useRef, useMemo, useContext } from "react";
import { secureGet, secureDelete, secureGetPdf } from "../../config/api";
import { useHistory } from "react-router-dom";
import { PrimaryButton } from "../../components/PrimaryButton";
import { formatDateTime } from "../../utils/format";
import { useTranslation } from "react-i18next";
import { Helmet } from "react-helmet-async";
import { useLocation } from "react-router-dom";
import OptionsMenu from "../../components/OptionsMenu";
import Table from "../../components/Table";
import Filter from "../../components/Filter";
import DeletePopup from "../../components/DeletePopup";
import PageScroller from "../../components/PageScroller";
import LoadingLogo from "../../components/LoadingLogo";
import SnackbarNotification from '../../components/SnackbarNotification';
import UserContext from "../../Shared/UserContext";

import AddIcon from "../../icons/plusB.svg";
import DownloadIcon from "../../icons/downloadB.svg";
import EyeIcon from "../../icons/eye.svg";
import EditIcon from "../../icons/edit.svg";
import DeleteIcon from "../../icons/trashR.svg";

const Quotes = () => {
  const [, setQuotesData] = useState([]);
  const [currentPageData, setCurrentPageData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    clientId: null,
    commercialId: null,
    dateRange: {},
    needle: "",
  });
  const [clients, setClients] = useState([]);
  const [commercials, setCommercials] = useState([]);
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [pageSize, setPageSize] = useState(30);
  const history = useHistory();
  const [isDeletePopupVisible, setDeletePopupVisible] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const { t } = useTranslation();
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const topRef = useRef(null);
  const location = useLocation();
  const { state: { role } } = useContext(UserContext);
  const user = JSON.parse(localStorage.getItem("user") || "{}");
  const isCommercial = role === "ROLE_COMMERCIAL";

  useEffect(() => {
    if (location.state?.showSnackbar) {
      showSnackbar(location.state.snackbarMessage, location.state.snackbarSeverity);
      history.replace({ ...location, state: undefined });
    }
  }, [location, history]);


  const showSnackbar = (message, severity = 'success', manual = false) => {
    setSnackbar({ open: true, message, severity, manual });
  };

  const abortController = useMemo(() => new AbortController(), []);
  const { signal } = abortController;

  const columns = [
    { label: t(`columns.quoteNumber`), key: "quoteNumber" },
    {
      label: t(`columns.createdAt`), key: "createdAt",
      render: (date) => formatDateTime(date)
    },
    { label: "Client", key: "client", render: (client) => client?.name || "N/A", flag: false },
    { label: t(`columns.commercial`), key: "user", render: (user) => `${user.firstName} ${user.lastName}` },
    { label: "Total HT", key: "priceHT", },
    { label: "Total TTC", key: "priceTTC", },
    {
      key: "actions",
      render: (_, rowData) =>
        <div onClick={(e) => e.stopPropagation()}>
          <OptionsMenu options={getOptions(rowData)} />
        </div>
    },
  ];

  const getOptions = (item) => {
    const baseOptions = [
      { label: t(`actions.details`), icon: EyeIcon, action: () => handleDetails(item) },
    ];

    const adminOptions = [
      { label: t(`actions.modify`), icon: EditIcon, action: () => handleEdit(item) },
      { label: t(`actions.downloadQuote`), icon: DownloadIcon, action: () => handleDownload(item) },
      { label: t(`actions.delete`), color: '#EF466F', icon: DeleteIcon, action: () => handleDelete(item) },
    ];

    return role === 'ROLE_ADMIN' ? [...baseOptions, ...adminOptions] : baseOptions;
  };


  const filterOptions = [
    {
      name: "needle",
      type: "search",
    },
    {
      name: "dateRange",
      type: "daterange",
      get: true,
    },
    {
      name: "clientId",
      label: "Client",
      type: "dropdown",
      options: [...clients.map((client) => ({ value: client.id, reference: client.code, name: client.name }))],
    },
    {
      name: "commercialId",
      label: t(`filters.orders.commercial`),
      type: "dropdown",
      options: isCommercial
        ? [
          {
            value: user.id,
            label: `${user.firstName} ${user.lastName}`,
          },
        ]
        : commercials.map((commercial) => ({
          value: commercial.id,
          label: `${commercial.firstName} ${commercial.lastName}`,
        })),
    },
  ];

  useEffect(() => {
    const fetchDropdownOptions = async () => {
      try {
        const clientResponse = await secureGet("/clients", showSnackbar, { signal });
        setClients(clientResponse.data);

        if (!isCommercial) {
          const commercialResponse = await secureGet("/users/ROLE_COMMERCIAL", showSnackbar, { signal });
          setCommercials(commercialResponse.data);
        }
      } catch (error) {
        if (error.name !== 'AbortError') {
          console.error("Error fetching dropdown options:", error);
        }
      }
    };
    fetchDropdownOptions();
    return () => {
      abortController.abort();
    };
  }, [abortController, signal]);

  const fetchPaginatedQuotes = useCallback(async (page = 0, size = pageSize, activeFilters = filters) => {
    setLoading(true);

    try {
      const params = new URLSearchParams();
      params.set('page', page.toString());
      params.set('size', size.toString());

      if (activeFilters.needle) {
        params.set('needle', activeFilters.needle);
      }

      if (activeFilters.clientId && activeFilters.clientId !== null) {
        params.set('clientId', activeFilters.clientId.toString());
      }
      if (role !== "ROLE_COMMERCIAL") {

        if (activeFilters.commercialId && activeFilters.commercialId !== null) {
          params.set('commercialId', activeFilters.commercialId.toString());
        }
      }

      if (activeFilters.startDate) {
        params.set('startDate', activeFilters.startDate);
      }

      if (activeFilters.endDate) {
        params.set('endDate', activeFilters.endDate);
      }

      params.set('sort', 'createdAt,desc');

      const endpoint = role === "ROLE_COMMERCIAL" ? '/quotes/mine' : '/quotes';

      const response = await secureGet(`${endpoint}?${params.toString()}`, showSnackbar, {
        signal,
      });


      const { content, totalPages } = response.data;
      setQuotesData(content);
      setCurrentPageData(content);
      setTotalPages(totalPages);
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error("Error fetching Quotes:", error);
      }
    } finally {
      setLoading(false);
    }
  }, [filters, pageSize, signal, role]);


  useEffect(() => {
    const stateFilters = location.state?.filters || {
      clientId: null,
      commercialId: null,
      dateRange: {},
      needle: "",
    };
    const statePage = location.state?.page ?? 0;
    const stateSize = location.state?.size ?? 30;

    setFilters(stateFilters);
    setCurrentPage(statePage);
    setPageSize(stateSize);

    fetchPaginatedQuotes(statePage, stateSize, stateFilters);
  }, []);


  const handlePageChange = (page) => {
    setCurrentPage(page);
    fetchPaginatedQuotes(page, pageSize, filters);
    if (topRef.current) {
      topRef.current.scrollIntoView({ behavior: "smooth" });
    }
  };

  const handlePageSizeChange = (size) => {
    setPageSize(size);
    setCurrentPage(0);
    fetchPaginatedQuotes(0, size, filters);
  };

  const handleFilterChange = (filterName, filterValue) => {
    const updatedFilters = { ...filters };

    if (filterName === "dateRange") {
      const { startDate, endDate } = filterValue || {};
      updatedFilters.startDate = startDate || "";
      updatedFilters.endDate = endDate || "";
    } else {
      updatedFilters[filterName] = filterValue;
    }

    setFilters(updatedFilters);
    setCurrentPage(0);
    fetchPaginatedQuotes(0, pageSize, updatedFilters);
  };


  const resetFilters = () => {
    const defaultFilters = {
      clientId: null,
      commercialId: null,
      dateRange: {},
      needle: "",
    };
    setFilters(defaultFilters);
    setCurrentPage(0);
    fetchPaginatedQuotes(0, pageSize, defaultFilters);
  };

  const handleDelete = (item) => {
    setItemToDelete(item);
    setDeletePopupVisible(true);
  };

  const handleDeleteConfirm = async () => {
    if (!itemToDelete) return;
    setDeleting(true);
    try {
      await secureDelete(`/quotes/delete/${itemToDelete.id}`, showSnackbar);
      setDeletePopupVisible(false);
      setItemToDelete(null);
      fetchPaginatedQuotes(currentPage, pageSize);
      showSnackbar(t(`confirmation.quoteDelete`), 'success');
    } catch (error) {
      console.error("Error deleting item:", error);
      setDeletePopupVisible(false);
    } finally {
      setDeleting(false);
      setItemToDelete(null);
    }
  };

  const handleDetails = (item) => {
    history.push({
      pathname: `/quotes/details/${item.id}`,
      state: { filters, page: currentPage, size: pageSize },
    });
  };

  const handleEdit = (item) => {
    history.push({
      pathname: `/quotes/modify/${item.id}`,
      state: { filters, page: currentPage, size: pageSize },
    });
  };

  const handleDownload = async (item) => {
    try {
      const response = await secureGetPdf(`/quotes/pdf/${item.id}`, showSnackbar);

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `quote/${item.id}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      showSnackbar(t('confirmation.downloadSuccess'), 'success');
    } catch (error) {
      console.error("Failed to download DV:", error);
    }
  };

  return (
    <div ref={topRef}>
      <Helmet>
        <title>GrayFox - {t("titles.quotes")}</title>
      </Helmet>
      <div className="flex items-center mb-[15px] ">
        <h1 className="text-[40px] font-semibold">{t(`titles.quotes`)}</h1>
        {(role === "ROLE_ADMIN" || role === "ROLE_COMMERCIAL") && (
          <div className="ml-auto text-[16px]">
            <PrimaryButton
              label={t(`primaryButtons.add_quote`)}
              icon={AddIcon}
              onClick={() => history.push({
              pathname: "/quotes/add",
              state: { filters, page: currentPage, size: pageSize },
            })}
          />
          </div>
        )}
      </div>

      <Filter
        filters={filterOptions}
        onFilterChange={(filterName, filterValue) => handleFilterChange(filterName, filterValue)}
        resetFilters={resetFilters}
        filterValues={filters}
      />
      <div style={{ position: "relative", minHeight: "200px" }}>
        {loading && (
          <div
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              backgroundColor: "rgba(255, 255, 255, 0.8)",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              zIndex: 1,
            }}
          >
            <LoadingLogo />
          </div>
        )}
        <Table
          columns={columns}
          data={currentPageData}
          onRowClick={(row) => {
            history.push({
              pathname: `/quotes/details/${row.id}`,
              state: { filters, page: currentPage, size: pageSize },
            });
          }}
        />
      </div>

      <PageScroller
        pageNumber={currentPage}
        totalPages={totalPages}
        pageSize={pageSize}
        onPageChange={handlePageChange}
        onPageSizeChange={handlePageSizeChange}
      />

      {isDeletePopupVisible && (
        <DeletePopup open={isDeletePopupVisible} onCancel={() => setDeletePopupVisible(false)} onConfirm={handleDeleteConfirm} loading={deleting} />
      )}

      <SnackbarNotification
        open={snackbar.open}
        message={snackbar.message}
        severity={snackbar.severity}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        manual={snackbar.manual}
      />
    </div>
  );
};

export default Quotes;

